import React, { useState, useMemo } from 'react';
import { Box, Button, makeStyles, FormControl, Select, MenuItem, Typography } from '@material-ui/core';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import { useTranslation } from 'react-i18next';
import AtSelectPeriod from '../../../components/AtSelectPeriod/AtSelectPeriod';
import { subMonths } from 'date-fns';

const useStyles = makeStyles((theme) => ({
    container: {
        backgroundColor: '#FFFFFF',
        padding: theme.spacing(2, 3),
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        marginBottom: theme.spacing(3),
    },
    filtersRow: {
        display: 'flex',
        alignItems: 'flex-end',
        flexWrap: 'wrap',
        gap: theme.spacing(2),
    },
    filterItem: {
        flex: '1 1 160px',
        minWidth: '140px',
        '& .MuiCard-root': {
            width: '100%'
        }
    },
    filterLabel: {
        fontSize: '11px',
        fontWeight: 700,
        color: '#9CA3AF',
        textTransform: 'uppercase',
        letterSpacing: '0.03em',
        marginBottom: '6px',
    },
    formControl: {
        '& .MuiOutlinedInput-root': {
            borderRadius: '6px',
            '& fieldset': {
                borderColor: '#E5E7EB',
            },
        },
        '& .MuiSelect-select': {
            fontSize: '13px',
            color: '#374151',
            padding: '10px 14px',
        },
    },
    separatorIcon: {
        color: '#9CA3AF',
        marginBottom: '8px',
        marginLeft: '-4px',
        marginRight: '-4px',
    },
    divider: {
        width: '1px',
        backgroundColor: '#E5E7EB',
        height: '40px',
        marginBottom: '4px',
        marginLeft: '8px',
        marginRight: '8px',
    },
    actions: {
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        gap: theme.spacing(2),
        marginTop: theme.spacing(2),
    },
    btnClean: {
        color: '#6366F1',
        textTransform: 'none',
        fontWeight: 600,
        fontSize: '13px',
        '&:hover': {
            backgroundColor: 'transparent',
            textDecoration: 'underline',
        },
    },
    btnApply: {
        backgroundColor: '#6366F1',
        color: '#FFFFFF',
        textTransform: 'none',
        fontWeight: 600,
        fontSize: '13px',
        padding: '6px 16px',
        borderRadius: '6px',
        '&:hover': {
            backgroundColor: '#4F46E5',
        },
    },
}));

const FiltersSection = ({ filters, activeFilters, onApply, onClear }) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const [pendingFilters, setPendingFilters] = useState(activeFilters);
    const [resetKey, setResetKey] = useState(0);

    const apps = filters?.apps || [];

    const availablePlatforms = useMemo(() => {
        if (!pendingFilters?.selectedApp) return [];
        const selectedApp = apps.find(a => a.id === pendingFilters.selectedApp);
        return selectedApp?.platforms || [];
    }, [pendingFilters?.selectedApp, apps]);

    const availableVersions = useMemo(() => {
        if (!pendingFilters?.selectedPlatform) return [];
        const selectedPlatform = availablePlatforms.find(p => p.id === pendingFilters.selectedPlatform);
        return selectedPlatform?.versions || [];
    }, [pendingFilters?.selectedPlatform, availablePlatforms]);

    const handleChange = (key, value) => {
        setPendingFilters(prev => {
            const next = { ...prev, [key]: value };

            if (key === 'selectedApp') {
                next.selectedPlatform = '';
                next.selectedVersion = '';
            }

            if (key === 'selectedPlatform') {
                next.selectedVersion = '';
            }

            return next;
        });
    };

    const handleClear = () => {
        const endDate = new Date();
        const startDate = subMonths(endDate, 1);
        const defaultFilters = {
            selectedApp: apps.length > 0 ? apps[0].id : '',
            selectedPlatform: '',
            selectedVersion: '',
            selectedPeriod: {
                value: "custom",
                text: "Último mes",
                period: "c",
                statePeriod: { startDate, endDate, key: "selection" }
            }
        };
        setPendingFilters(defaultFilters);
        setResetKey(prev => prev + 1);
        onClear();
    };

    return (
        <Box className={classes.container}>
            <Box className={classes.filtersRow}>

                {/* App selector */}
                <Box className={classes.filterItem}>
                    <Typography className={classes.filterLabel}>{t('sentiment.filters.app')}</Typography>
                    <FormControl variant="outlined" size="small" fullWidth className={classes.formControl}>
                        <Select
                            value={pendingFilters?.selectedApp || ''}
                            onChange={(e) => handleChange('selectedApp', e.target.value)}
                            displayEmpty
                            renderValue={(selected) => {
                                if (!selected) return <span style={{ color: '#9CA3AF' }}>{t('sentiment.filters.selectApp')}</span>;
                                const app = apps.find(a => a.id === selected);
                                return app?.name || selected;
                            }}
                        >
                            {apps.map((app) => (
                                <MenuItem key={app.id} value={app.id}>{app.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                <ChevronRightIcon className={classes.separatorIcon} />

                {/* Platform (Store) selector - depends on selected app */}
                <Box className={classes.filterItem}>
                    <Typography className={classes.filterLabel}>{t('sentiment.filters.store')}</Typography>
                    <FormControl variant="outlined" size="small" fullWidth className={classes.formControl}>
                        <Select
                            value={pendingFilters?.selectedPlatform || ''}
                            onChange={(e) => handleChange('selectedPlatform', e.target.value)}
                            displayEmpty
                            disabled={availablePlatforms.length === 0}
                        >
                            <MenuItem value="">{t('sentiment.filters.allStores')}</MenuItem>
                            {availablePlatforms.map((platform) => (
                                <MenuItem key={platform.id} value={platform.id}>{platform.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                <ChevronRightIcon className={classes.separatorIcon} />

                <Box className={classes.filterItem}>
                    <Typography className={classes.filterLabel}>{t('sentiment.filters.version')}</Typography>
                    <FormControl variant="outlined" size="small" fullWidth className={classes.formControl}>
                        <Select
                            value={pendingFilters?.selectedVersion || ''}
                            onChange={(e) => handleChange('selectedVersion', e.target.value)}
                            displayEmpty
                            disabled={availableVersions.length === 0}
                        >
                            <MenuItem value="">{t('sentiment.filters.allVersions')}</MenuItem>
                            {availableVersions.map((version) => (
                                <MenuItem key={version.id} value={version.id}>{version.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                <Box className={classes.divider} />

                <Box className={classes.filterItem}>
                    <Typography className={classes.filterLabel}>{t('sentiment.filters.period')}</Typography>
                    <AtSelectPeriod
                        key={`period-reset-${resetKey}`}
                        activePeriodFromParent={
                            pendingFilters?.selectedPeriod || {
                                value: "custom",
                                text: "Último mes",
                                period: "c",
                                statePeriod: { startDate: subMonths(new Date(), 1), endDate: new Date(), key: "selection" }
                            }
                        }
                        persistInStorage={false}
                        hideApplyToAll={true}
                        changePeriodFn={(val) => handleChange('selectedPeriod', val)}
                    />
                </Box>
            </Box>

            <Box className={classes.actions}>
                <Button className={classes.btnClean} onClick={handleClear}>
                    {t('sentiment.filters.clearFilters')}
                </Button>
                <Button className={classes.btnApply} variant="contained" onClick={() => onApply(pendingFilters)}>
                    {t('sentiment.filters.applyAnalytics')}
                </Button>
            </Box>
        </Box>
    );
};

export default FiltersSection;
